/**
 * platformContext.ts
 *
 * Primes the Orbi orb with what is ON SCREEN so the "Ask me anything" panel
 * knows the operator's current vantage point before a single word is typed.
 *
 * Two sources, both read at call time (never cached) so a lens or section
 * change is always reflected:
 *   1. window.ORA.scope    - the canonical scope resolver (ora-config.js):
 *        altitude, vertical_id, client_id, org_id, client_slug, client_name,
 *        provider, section. This is the SUBJECT of the screen (Rule 32),
 *        derived from the URL altitude, not from a viewport toggle.
 *   2. window.__ORBI_PLATFORM__ - an optional bag the host page populates with
 *        live on-screen data (D.* style counts, the record in view, the active
 *        ISP). Pages push to it; the orb reads it. Absent on pages that have
 *        not wired it yet, in which case the orb degrades to scope only.
 *
 * Nothing here writes. Nothing here invents. If a field is missing it stays
 * undefined so the grounded answer never claims a number we did not read.
 */

import { workspaceWindow } from './workspace';

export interface OrbiScope {
  altitude: 'client' | 'vertical';
  vertical_id: string | null;
  vertical_slug: string | null;
  client_id: string | null;
  client_slug: string | null;
  client_name: string | null;
  org_id: string | null;
  provider: string | null;
  section: string | null;
}

export interface OrbiClientRef {
  slug: string;
  name: string;
  id?: string;
}

export interface PlatformContext {
  scope: OrbiScope;
  /** Free-form on-screen summary the host page pushed to window.__ORBI_PLATFORM__. */
  on_screen: Record<string, unknown>;
  /** Known clients in this vertical, for navigation grounding. */
  clients: OrbiClientRef[];
}

/* PILOT CONSTANTS, NOT DEFAULTS (tightened 2026-08-27). These are Teglo's own
   identifiers and they are the fallback for exactly ONE case: a bare pilot
   route (/customers, /network, ...) where the URL names no altitude at all and
   the pilot vertical IS the subject. The moment the URL names a vertical or a
   client, an unresolved scope FAILS CLOSED to null instead of falling back
   here - see readScope below. */
const TEGLO_VERTICAL_ID = 'f0000001-0000-0000-0000-000000000000';
const TEGLO_ORG_ID = 'f0000000-0000-0000-0000-000000000000';

/* JARVIS NAV: while the in-place workspace layer is up (a live call navigated
   without a document load), the operator's SCREEN is the framed page, so all
   context reads source from that window - same read paths, same honesty rules.
   When the layer is down this is exactly `window`, byte for byte. */
function srcWindow(): any {
  if (typeof window === 'undefined') return null;
  try {
    const w = workspaceWindow();
    // Only trust the frame once its ORA scope resolver has booted; until then
    // the parent window is still the truthful vantage point.
    if (w && (w as any).ORA) return w;
  } catch { /* frame not readable yet */ }
  return window;
}

/* DROP-IN (Phase 3 binding 4): the one-script-tag config surface. A page with
   no ORA shell declares its identity once:
     window.__ORBI_ENV__.ORBI_SCOPE = { altitude, vertical_id, vertical_slug,
       client_id, client_slug, client_name, org_id, provider, section,
       clients: [{slug,name,id}] }
   The shell, when present, stays authoritative field by field; the config bag
   fills what the shell does not provide; the Teglo constants remain the last
   fallback so every existing page is byte-compatible. */
function envScope(): any {
  try {
    const env: any = (typeof window !== 'undefined' && (window as any).__ORBI_ENV__) || {};
    return env.ORBI_SCOPE && typeof env.ORBI_SCOPE === 'object' ? env.ORBI_SCOPE : {};
  } catch { return {}; }
}

/* ── URL ALTITUDE (fail-closed tenant binding, operator repro 2026-08-27) ──
   The operator opened a freshly spun vertical and the dock was still bound to
   Conduct Connect: its notifications and its Jarvis context read the PILOT's
   rows while the address bar said someone else's name.

   Cause: for an unregistered slug config/registry.js serves the neutral shell,
   whose CFG.id is null by design, so window.ORA.scope.verticalId is null - and
   every reader here treated null as "use the pilot". A missing id is not a
   licence to substitute another tenant's; it is a reason to show nothing.

   This resolves the altitude from the URL ITSELF (the same two shapes
   registry.js matches) so the dock can tell the two cases apart:
     - no altitude in the URL  -> a bare pilot route; the pilot constants stand.
     - an altitude in the URL  -> the scope must RESOLVE and AGREE with it, or
                                  every tenant-keyed field is null and the
                                  readers stay dark.
   Read at call time like everything else here, and through srcWindow() so the
   framed workspace page is the vantage point while a call is navigating. */
export interface UrlAltitude {
  kind: 'vertical' | 'client';
  slug: string;
}

export function urlAltitude(): UrlAltitude | null {
  try {
    const path = String(srcWindow()?.location?.pathname || '');
    const c = path.match(/^\/c\/([^/]+)(?:\/|$)/);
    if (c) return { kind: 'client', slug: c[1] };
    const v = path.match(/^\/v\/([^/]+)(?:\/|$)/);
    if (v) return { kind: 'vertical', slug: v[1] };
  } catch { /* non-browser env: no URL to read, so no altitude claim */ }
  return null;
}

/** True when the resolved scope actually IS the tenant the URL names. A slug
 *  mismatch (the neutral shell's '_neutral', a stale client registry) is a
 *  failure to resolve, not a near miss. */
function scopeMatchesUrl(alt: UrlAltitude, verticalSlug: string | null, clientSlug: string | null): boolean {
  if (alt.kind === 'client') return !!clientSlug && clientSlug === alt.slug;
  return !!verticalSlug && verticalSlug === alt.slug;
}

/** The tenant identity is unresolved: the URL names a vertical or client that
 *  the shell could not turn into ids. Readers use this to stay dark rather
 *  than read the pilot. False on bare pilot routes, which resolve by
 *  definition. */
export function tenantUnresolved(): boolean {
  const alt = urlAltitude();
  if (!alt) return false;
  const s = readScope();
  return !s.vertical_id || !scopeMatchesUrl(alt, s.vertical_slug, s.client_slug);
}

export function readScope(): OrbiScope {
  const host: any = srcWindow();
  const ORA: any = host?.ORA || null;
  const s = ORA?.scope || {};
  const cfg = envScope();
  const verticalSlug = s.verticalSlug || cfg.vertical_slug || null;
  const clientSlug = s.clientSlug || cfg.client_slug || null;
  /* FAIL CLOSED (2026-08-27): the pilot constants apply only when the URL
     names no altitude. Under /v/<slug>/ or /c/<slug>/ the shell (or the
     drop-in config bag) must supply the ids AND agree with the slug in the
     URL; anything else leaves them null so a reader shows nothing rather than
     the pilot's rows under another tenant's name. */
  const alt = urlAltitude();
  const bare = !alt;
  const agrees = bare || scopeMatchesUrl(alt!, verticalSlug, clientSlug);
  const verticalId = s.verticalId || cfg.vertical_id || (bare ? TEGLO_VERTICAL_ID : null);
  const orgId = s.orgId || cfg.org_id || (bare ? TEGLO_ORG_ID : null);
  return {
    altitude: (s.altitude || cfg.altitude) === 'client' ? 'client' : 'vertical',
    vertical_id: agrees ? verticalId : null,
    vertical_slug: verticalSlug,
    client_id: agrees ? (s.clientId || cfg.client_id || null) : null,
    client_slug: clientSlug,
    client_name: s.clientName || cfg.client_name || null,
    org_id: agrees ? orgId : null,
    provider: s.provider || cfg.provider || null,
    section: s.section || cfg.section || null,
  };
}

export function readClients(): OrbiClientRef[] {
  const ORA: any = srcWindow()?.ORA || null;
  const reg = ORA?.clients || {};
  const out: OrbiClientRef[] = [];
  Object.keys(reg).forEach((k) => {
    const c = reg[k];
    if (c && c.slug && c.name) out.push({ slug: c.slug, name: c.name, id: c.id });
  });
  if (out.length) return out;
  // No shell roster: the config bag may carry one (drop-in pages).
  const cfgClients = envScope().clients;
  if (Array.isArray(cfgClients)) {
    cfgClients.forEach((c: any) => {
      if (c && c.slug && c.name) out.push({ slug: String(c.slug), name: String(c.name), id: c.id ? String(c.id) : undefined });
    });
  }
  return out;
}

/** The host page's own intent registry (components/canonical/ora-context-envelope.js,
 *  polish wave 12). A read question the page can answer from what is on screen is
 *  answered there, in the browser, before the dock's own readback and before any
 *  backend ask. Null when the page has no registry or no intent fits. */
export function pageIntentAnswer(text: string): string | null {
  try {
    const oc: any = srcWindow()?.OraContext;
    const a = oc?.intents?.answer?.(text);
    return typeof a === 'string' && a ? a : null;
  } catch (_) { return null; }
}

export function readPlatformContext(): PlatformContext {
  const bag: any = srcWindow()?.__ORBI_PLATFORM__ || {};
  return {
    scope: readScope(),
    on_screen: bag && typeof bag === 'object' ? bag : {},
    clients: readClients(),
  };
}

/** One honest line for the panel context chip. Names only what we actually read. */
export function describeContext(ctx: PlatformContext): string {
  const parts: string[] = [];
  if (ctx.scope.section) parts.push(cap(ctx.scope.section));
  parts.push(ctx.scope.client_name || (ctx.scope.vertical_slug ? cap(ctx.scope.vertical_slug) + ' (all clients)' : 'All clients'));
  const os: any = ctx.on_screen || {};
  if (typeof os.records === 'number') parts.push(os.records + ' on screen');
  else if (typeof os.account_count === 'number') parts.push(os.account_count + ' subscribers');
  return parts.join(' · ');
}

function cap(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** The orb identity, derived from the ACTIVE LENS, never hardcoded. The platform
 *  orb is always Orbi in OPERATOR mode; the subtitle names whose console this is -
 *  a specific client (e.g. "Operator - Conduct Connect") or the vertical HQ across
 *  all clients (e.g. "Operator - Teglo HQ"). One brain, scoped to the operator's
 *  altitude and client. */
export function readIdentity(scope: OrbiScope): { title: string; subtitle: string; scopeLabel: string } {
  let where: string;
  if (scope.altitude === 'client' && scope.client_name) where = scope.client_name;
  else if (scope.vertical_slug) where = `${cap(scope.vertical_slug)} HQ`;
  else where = 'all clients';
  return { title: 'AI assistant', subtitle: `Operator · ${where}`, scopeLabel: where };
}

/** The operator voice context: the on-screen scope and section, plus (UX pass,
 *  Eri review) the lens, the known clients, an optional explicitly-open subject
 *  and the recent-section trail. Used to prime EVERY dock-initiated voice call
 *  as the operator copilot (mode 'operator'): the dock is the staff console, so
 *  the agent talks to staff, never runs a customer script, and never asks the
 *  operator to identify themselves. Subject carries name + account number only
 *  (the same two fields the panel already shows), no further PII. */
export function buildOperatorVoiceContext(
  ctx: PlatformContext,
  extras?: {
    subject?: { name?: string | null; account_number?: string | null } | null;
    recent_sections?: string | null;
    operator_name?: string | null;
    /** ONE PATH (audit A1): the last few typed-panel turns, so a voice call
     *  starting mid-conversation inherits what typed Jarvis just did. */
    recent_thread?: string | null;
  }
): Record<string, unknown> {
  const id = readIdentity(ctx.scope);
  return {
    account_number: 'OPERATOR',
    first_name: '', last_name: '',
    plan_name: '', plan_speed_mbps: 0, current_speed_mbps: 0,
    connection_status: 'n/a', devices_online: 0, billing_status: 'n/a',
    port_id: null, switch_id: null, diagnostics: {}, open_tickets: [],
    persona: { persona_key: 'customer_support' },
    _operator: {
      scope_label: id.subtitle.replace(/^Operator · /, ''),
      section: ctx.scope.section || null,
      altitude: ctx.scope.altitude,
      client_name: ctx.scope.client_name || null,
      // ALTITUDE (tenant isolation): the cross-client roster is consolidated
      // operator-vertical context ONLY. At client altitude - reachable by that
      // client's own staff, not just an operator drilling in - Jarvis must be
      // scoped to this one client and never briefed with other clients' names.
      clients: ctx.scope.altitude === 'client' ? [] : ctx.clients.map((c) => c.name).slice(0, 8),
      subject: extras?.subject && (extras.subject.name || extras.subject.account_number)
        ? { name: extras.subject.name || null, account_number: extras.subject.account_number || null }
        : null,
      recent_sections: extras?.recent_sections || null,
      operator_name: extras?.operator_name || null,
      recent_thread: extras?.recent_thread || null,
    },
  };
}

/** Discrete chips for the hero "Primed with whats on screen" row. Only emits
 *  metrics actually present on screen, never invented. Ordered scale -> operations
 *  -> memory -> risk -> value so the row leads with reach, not churn. Each carries
 *  an optional title so an ambiguous figure (e.g. savings) is never bare. */
/** The workspace's own words for the terms these chips name, singular and plural
 *  (public.vertical_vocabulary through ORA.vocabulary(), resolved by the dock).
 *  Absent means the platform's vertical-neutral words, which are never ISP nouns. */
export type ChipWords = Partial<Record<'subscriber' | 'property' | 'ticket', { one: string; many: string }>>;
const NEUTRAL_CHIP_WORDS: Required<ChipWords> = {
  subscriber: { one: 'customer', many: 'customers' },
  property: { one: 'site', many: 'sites' },
  ticket: { one: 'request', many: 'requests' },
};

/* THE CHIPS SPEAK THE WORKSPACE'S LANGUAGE (D7a, 2026-09-18). This row said
   "0 customers", "12 buildings" and "3 open tickets" on every workspace, so the
   very first thing a clinic's operator read from the assistant, above the
   greeting, was the ISP's vocabulary for their patients. Rule 6 made those words
   rows; this is the last chip row that was still typing them. */
export function readChips(ctx: PlatformContext, words?: ChipWords): Array<{ label: string; title?: string }> {
  const os: any = ctx.on_screen || {};
  const chips: Array<{ label: string; title?: string }> = [];
  const n = (v: any) => Number(v).toLocaleString('en-US');
  const w = (k: 'subscriber' | 'property' | 'ticket', form: 'one' | 'many') =>
    ((words && words[k] && words[k]![form]) || NEUTRAL_CHIP_WORDS[k][form]).toLowerCase();
  const Cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
  // Scale
  if (typeof os.account_count === 'number') chips.push({ label: `${n(os.account_count)} ${w('subscriber', 'many')}`, title: `${Cap(w('subscriber', 'many'))} in view` });
  else if (typeof os.records === 'number') chips.push({ label: `${n(os.records)} on screen`, title: 'Records in view' });
  if (typeof os.active_subscribers === 'number') chips.push({ label: `${n(os.active_subscribers)} active`, title: `Active ${w('subscriber', 'many')} in view` });
  // Operations
  if (typeof os.open_tickets === 'number') chips.push({ label: `${n(os.open_tickets)} open ${w('ticket', 'many')}`, title: `Open ${w('ticket', 'many')} in view` });
  else if (typeof os.tickets === 'number') chips.push({ label: `${n(os.tickets)} ${w('ticket', 'many')}`, title: `${Cap(w('ticket', 'many'))} in view` });
  if (typeof os.buildings === 'number') chips.push({ label: `${n(os.buildings)} ${w('property', 'many')}`, title: `${Cap(w('property', 'many'))} in view` });
  // Memory
  if (typeof os.patterns === 'number') chips.push({ label: `${n(os.patterns)} patterns`, title: 'Memory patterns the AI has formed for this scope' });
  // Risk (kept after the positives so the row never opens on churn)
  if (typeof os.churned === 'number') chips.push({ label: `${n(os.churned)} churned`, title: `Churned ${w('subscriber', 'many')} in view` });
  // Value (ambiguous on its own, so it is always labelled and never leads)
  if (typeof os.mrr_usd === 'number' && os.mrr_usd > 0) chips.push({ label: `$${n(os.mrr_usd)} MRR`, title: 'Monthly recurring revenue in view' });
  if (typeof os.savings_usd === 'number' && os.savings_usd > 0) chips.push({ label: `$${n(os.savings_usd)} saved`, title: 'Estimated cost the AI has saved by resolving issues without a human (all time)' });
  if (os.record_in_view && os.record_in_view.name) chips.push({ label: String(os.record_in_view.name), title: 'The record open on screen' });
  return chips;
}

/** Detect an explicit "what is on this screen" style question so the orb can
 *  answer it directly from the on-screen envelope (rule 32 pre-emptive context),
 *  with no backend round trip and nothing invented. Kept tight so ordinary
 *  questions still route to the grounded ask backend. */
export function isWhatOnScreenQuery(text: string): boolean {
  const t = (text || '').toLowerCase().trim().replace(/[?.!]+$/, '');
  if (!t) return false;
  if (/^(where am i|what page am i on|what section am i on)$/.test(t)) return true;
  return /\b(what|whats|what's|which|describe|tell me|show me|summar\w+)\b.{0,40}\b(on (this |the )?(screen|page|view)|looking at|am i seeing|in view|this (screen|page|view)|on screen)\b/.test(t)
      || /^what am i looking at$/.test(t);
}

/* Humanised labels for the numeric on-screen fields a page publishes. Only keys
   present in the envelope are ever surfaced, so an unwired page stays silent
   rather than reporting a zero it never measured. Exported (2026-08-26) so the
   voice situation brief renders the SAME envelope keys the typed panel does -
   the brief used to know only the network/customers keys, which is why the
   panel could show bookings/leads/costs chips while the voice agent honestly
   said it had no figures for the page. One label map, no duplication. */
export const ON_SCREEN_LABELS: Record<string, string> = {
  records: 'records', account_count: 'customers', active_subscribers: 'active subscribers',
  buildings: 'buildings', open_tickets: 'open tickets', tickets: 'tickets',
  patterns: 'patterns', churned: 'churned', memory_findings: 'memory findings',
  bookings: 'bookings', leads: 'leads', engineers: 'engineers', orbi_booked: 'booked by the AI',
  cost_events: 'cost events',
};
export const MONEY_LABELS: Record<string, string> = {
  mrr_usd: 'MRR', savings_usd: 'saved', spend_usd: 'spend',
};

/** A grounded, spoken-plain answer to "what am I looking at", composed only from
 *  the published envelope (scope + section + the counts the page actually holds +
 *  the record open in a slide-out). Never fabricates a figure. */
export function answerWhatOnScreen(ctx: PlatformContext): string {
  const scope = ctx.scope;
  const os: any = ctx.on_screen || {};
  const n = (v: any) => Number(v).toLocaleString('en-US');
  const where = (scope.altitude === 'client' && scope.client_name)
    ? scope.client_name
    : scope.vertical_slug ? `${cap(scope.vertical_slug)} HQ, all clients` : 'all clients';
  const sectionName = scope.section ? cap(scope.section) : 'the dashboard';

  const parts: string[] = [];
  Object.keys(ON_SCREEN_LABELS).forEach((k) => {
    // account_count is the specific subscriber count; records is the generic
    // fallback. When both are present they are the same figure, so drop the
    // generic one rather than say it twice.
    if (k === 'records' && typeof os.account_count === 'number') return;
    if (typeof os[k] === 'number') parts.push(`${n(os[k])} ${ON_SCREEN_LABELS[k]}`);
  });
  Object.keys(MONEY_LABELS).forEach((k) => {
    if (typeof os[k] === 'number' && os[k] > 0) parts.push(`$${n(os[k])} ${MONEY_LABELS[k]}`);
  });

  let line = `You are on the ${sectionName} view for ${where}.`;
  if (parts.length) line += ` On screen: ${parts.join(', ')}.`;
  const rec = os.record_in_view;
  if (rec && rec.name) {
    const tail = rec.account_number ? ` (${rec.account_number})` : '';
    line += ` Open right now: ${rec.name}${tail}.`;
  }
  if (!parts.length && !(rec && rec.name)) {
    line += ' I do not have on-screen figures wired for this view yet, so I can tell you the section and scope but not live counts.';
  }
  return line;
}

/** Operator first name for the hero greeting, derived from the auth session.
 *  Returns null when unauthenticated so the panel simply omits the greeting. */
export function readGreetingName(): string | null {
  try {
    const ORA: any = (typeof window !== 'undefined' && (window as any).ORA) || null;
    const email = ORA?.auth?.user?.email;
    if (email) {
      const lp = String(email).split('@')[0].split(/[._-]/)[0];
      return lp ? cap(lp) : null;
    }
  } catch { /* unauthenticated */ }
  return null;
}
